import React, {useEffect, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import momentZ from 'moment-timezone';
import MDSpinner from 'react-md-spinner';
import {AiFillAndroid, AiFillApple} from 'react-icons/ai';
import {AppSelector} from '../general/AppSelector';
import {SingleSelector} from '../general/SingleSelector';
import {Switcher} from '../general/Switcher';
import {colors} from '../../utils/StyleUtils';
import {FromToDatePicker} from '../general/FromToDatePicker';
import RefundSaverChart, {RefundSaverChartType} from './RefundSaverChart';
import {RefundSaverCases} from './RefundSaverCases';
import {requestMetricsWebsocket} from '../../utils/MetricsWebsocket';

const PRESETS = [
  {label: '7 days', amount: 7, unit: 'day'},
  {label: '1 month', amount: 1, unit: 'month'},
  {label: '6 months', amount: 6, unit: 'month'},
  {label: '1 year', amount: 1, unit: 'year'},
];
const GROUPS = [{value: 'day', label: '1 day'}, {value: 'week', label: '1 week'}, {value: 'month', label: '1 month'}];
const COLORS = {requests: '#00B4D8', refunded: '#F13838', saved: '#2EC27E'};
const STORAGE_KEY = 'refundSaverFilters';

const loadFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const presetRange = (preset) => ({
  fromDate: momentZ().subtract(preset.amount, preset.unit).add(1, 'day').format('YYYY-MM-DD'),
  toDate: momentZ().format('YYYY-MM-DD'),
});

// Postgres date_trunc buckets (weeks start on Monday), filled with zeros so the axis is continuous
const bucketStart = (date, group) => momentZ.utc(date).startOf(group === 'week' ? 'isoWeek' : group);
const fillBuckets = (buckets, fromDate, toDate, group) => {
  const byKey = {};
  buckets.forEach((b) => {
    byKey[bucketStart(b.bucket, group).toISOString()] = b;
  });
  const result = [];
  const end = momentZ.utc(toDate).endOf('day');
  for (let cursor = bucketStart(fromDate, group); cursor.isSameOrBefore(end); cursor = cursor.clone().add(1, group)) {
    const key = cursor.toISOString();
    result.push(byKey[key] || {bucket: key, requests: 0, refunded: 0, notRefunded: 0, reversed: 0, pending: 0, refundedUsd: 0, savedUsd: 0, pendingUsd: 0});
  }
  return result;
};

const usd = (v) => `$${Number(v || 0).toLocaleString('en-US', {maximumFractionDigits: 2})}`;
const percent = (v) => `${Number(v || 0).toFixed(1)}%`;

const RefundSaver = ({onelightApps}) => {
  const saved = loadFilters();
  const [appIds, setAppIds] = useState(saved.appIds || []);
  const [platform, setPlatform] = useState(saved.platform || 'all');
  const [range, setRange] = useState(saved.range || {...presetRange(PRESETS[1]), preset: PRESETS[1].label});
  const [group, setGroup] = useState(saved.group || 'day');
  const [chartType, setChartType] = useState(saved.chartType || RefundSaverChartType.LINES);
  const [moneyMode, setMoneyMode] = useState(saved.moneyMode || 'absolute');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const requestId = useRef(0);

  // One option per app (the list has a row per store)
  const apps = useMemo(() => {
    const unique = {};
    (onelightApps || []).forEach((app) => {
      if (app.appIdentifier && !unique[app.appIdentifier]) {
        unique[app.appIdentifier] = app;
      }
    });
    return Object.values(unique);
  }, [onelightApps]);

  const params = useMemo(() => ({
    from: momentZ.utc(range.fromDate).startOf('day').toISOString(),
    to: momentZ.utc(range.toDate).add(1, 'day').startOf('day').toISOString(),
    appIds,
    platforms: platform === 'all' ? [] : [platform],
  }), [range, appIds, platform]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({appIds, platform, range, group, chartType, moneyMode}));
    } catch (e) {
      // storage unavailable — filters are just not remembered
    }
  }, [appIds, platform, range, group, chartType, moneyMode]);

  useEffect(() => {
    const id = ++requestId.current;
    setIsLoading(true);
    setError(null);
    setSelectedBucket(null);
    requestMetricsWebsocket('REFUND_SAVER_STATS', {...params, group, stateId: String(id)}, 'REFUND_SAVER_STATS_RESULT')
        .then((result) => {
          if (id === requestId.current) {
            setStats(result);
          }
        })
        .catch((e) => id === requestId.current && setError(e.message))
        .finally(() => id === requestId.current && setIsLoading(false));
  }, [params, group]);

  const buckets = useMemo(() => (stats ? fillBuckets(stats.buckets, range.fromDate, range.toDate, group) : []),
      [stats, range, group]);
  const point = (b, y) => ({x: new Date(b.bucket), y, bucket: b.bucket});

  const requestsSeries = [
    {name: 'Refund requests', color: COLORS.requests, data: buckets.map((b) => point(b, b.requests))},
    {name: 'Refunded', color: COLORS.refunded, data: buckets.map((b) => point(b, b.refunded))},
  ];
  const moneySeries = moneyMode === 'absolute' ? [
    {name: 'Saved', color: COLORS.saved, data: buckets.map((b) => point(b, b.savedUsd))},
    {name: 'Refunded', color: COLORS.refunded, data: buckets.map((b) => point(b, b.refundedUsd))},
  ] : [
    {name: 'Saved, % of settled', color: COLORS.saved, data: buckets.map((b) => {
      const settled = b.savedUsd + b.refundedUsd;
      return point(b, settled ? (b.savedUsd / settled) * 100 : 0);
    })},
    {name: 'Refunded, % of settled', color: COLORS.refunded, data: buckets.map((b) => {
      const settled = b.savedUsd + b.refundedUsd;
      return point(b, settled ? (b.refundedUsd / settled) * 100 : 0);
    })},
  ];
  const totals = stats ? stats.totals : null;
  const settledUsd = totals ? totals.savedUsd + totals.refundedUsd : 0;
  // Selecting a bucket is not bound to a stale grouping
  const selectBucket = (bucket) => setSelectedBucket(bucket === selectedBucket ? null : bucket);

  const togglePlatform = (value) => setPlatform(platform === value ? 'all' : value);
  const platformIconClass = (value) => 'store-selector-icon' + (platform === 'all' || platform === value ? '-active' : '-unactive');
  const groupLabel = (GROUPS.find((g) => g.value === group) || GROUPS[0]).label;

  return <div className={'refund-saver-container'}>
    <div style={{display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 3}}>
      <div style={{display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-end'}}>
        <div style={{display: 'flex'}}>
          {[{value: RefundSaverChartType.LINES, label: 'Line'}, {value: RefundSaverChartType.BARS, label: 'Bar'}].map((item) => <div
            key={item.value}
            className={'metrics-header-tab' + (chartType === item.value ? '-active' : '')}
            onClick={() => setChartType(item.value)}
          >{item.label}</div>)}
        </div>
        <div style={{display: 'flex', alignItems: 'center', paddingBottom: '4px'}}>
          <div className={'days-picker-container'} style={{paddingRight: '16px'}}>
            {PRESETS.map((preset, index) => <React.Fragment key={preset.label}>
              {index > 0 && <div style={{paddingLeft: '3px', paddingRight: '3px'}}>/</div>}
              <div
                className={'days-label-picker'}
                style={{color: range.preset === preset.label ? '#00B4B4' : undefined}}
                onClick={() => setRange({...presetRange(preset), preset: preset.label})}
              >{preset.label}</div>
            </React.Fragment>)}
          </div>
          <FromToDatePicker
            style={{display: 'flex', alignItems: 'center'}}
            fromDate={range.fromDate}
            toDate={range.toDate}
            daysAgo={[7, 30, 180, 365]}
            changeFromDate={(date) => setRange({...range, fromDate: momentZ(date).format('YYYY-MM-DD'), preset: null})}
            changeToDate={(date) => setRange({...range, toDate: momentZ(date).format('YYYY-MM-DD'), preset: null})}
            changeLastDays={(fromDate, toDate) => setRange({fromDate, toDate, preset: null})}
          />
        </div>
      </div>
      <div className={'metrics-header-container'}>
        <div
          style={{paddingLeft: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '40px'}}
          title={'No platform selected = both'}
        >
          <AiFillAndroid className={platformIconClass('android')} onClick={() => togglePlatform('android')}/>
          <AiFillApple className={platformIconClass('ios')} onClick={() => togglePlatform('ios')}/>
        </div>
        <AppSelector
          style={{paddingLeft: '10px', minWidth: '260px'}}
          selectedApps={appIds}
          changeApps={setAppIds}
          allApps={apps}
          isAppIdentifier={true}
          placeholder={'All apps'}
        />
        <div className={'switcher-label-text'} style={{paddingLeft: '20px', paddingRight: '8px'}}>Group by</div>
        <SingleSelector
          style={{alignItems: 'center', width: '120px'}}
          selectedValue={group}
          selectedLabel={groupLabel}
          options={GROUPS}
          change={(value) => GROUPS.find((g) => g.value === value) && setGroup(value)}
        />
        {isLoading && <div style={{paddingLeft: '16px', display: 'flex'}}><MDSpinner size={22} singleColor={'#00DADA'}/></div>}
      </div>
    </div>

    {error && <div className={'refund-saver-error'}>{error}</div>}

    {totals && <div className={'aso-report-data-table refund-saver-totals'}>
      <div><span>Refund requests</span>{totals.requests}</div>
      <div><span>Refunded</span>{totals.refunded}{totals.reversed ? ` (+${totals.reversed} reversed)` : ''}</div>
      <div><span>Not refunded</span>{totals.notRefunded}</div>
      <div><span>Waiting for the store</span>{totals.pending} · {usd(totals.pendingUsd)}</div>
      <div><span>Money saved</span>{usd(totals.savedUsd)}{settledUsd ? ` · ${percent((totals.savedUsd / settledUsd) * 100)}` : ''}</div>
      <div><span>Money refunded</span>{usd(totals.refundedUsd)}</div>
    </div>}

    {!stats && isLoading && <div className='spinner-container' style={{marginTop: '150px'}}>
      <MDSpinner size={100} singleColor={'#00DADA'}/>
    </div>}

    {stats && <>
      <div className={'chart-container refund-saver-chart'}>
        <div className={'refund-saver-chart-title-row'}>
          <div className={'aso-report-item-header'}>Refund requests</div>
          <div className={'switcher-label-text'}>Click a point to see its requests</div>
        </div>
        <RefundSaverChart
          series={requestsSeries}
          chartType={chartType}
          group={group}
          yLabel={'Requests'}
          onSelect={selectBucket}
          selectedBucket={selectedBucket}
        />
      </div>
      <div className={'chart-container refund-saver-chart'}>
        <div className={'refund-saver-chart-title-row'}>
          <div className={'aso-report-item-header'}>Money saved</div>
          <div style={{display: 'flex', alignItems: 'center'}}>
            <div className={'switcher-label-text'} style={{paddingRight: '8px'}}>Relative to refunded</div>
            <Switcher
              isActive={moneyMode === 'relative'}
              colorOn={colors.defSwitcherActive}
              colorOff={colors.defSwitcherUnActive}
              switch={(isActive) => setMoneyMode(isActive ? 'relative' : 'absolute')}
              id={'refund-saver-money-mode'}
            />
          </div>
        </div>
        <RefundSaverChart
          series={moneySeries}
          chartType={chartType}
          group={group}
          yLabel={moneyMode === 'absolute' ? 'USD' : '%'}
          formatY={moneyMode === 'absolute' ? usd : percent}
          onSelect={selectBucket}
          selectedBucket={selectedBucket}
        />
      </div>
    </>}

    {selectedBucket && <RefundSaverCases params={params} bucket={selectedBucket} group={group}/>}
  </div>;
};

const mapStateToProps = (state) => ({onelightApps: state.appState.onelightApps});

export default connect(mapStateToProps)(RefundSaver);
